import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Checkbox} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Modal} from '@dropbox/dig-components/modal';
import {Popover} from '@dropbox/dig-components/popover';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {DeleteLine, InfoLine, SearchLine} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {snackbarAtom} from 'atoms/snackbar';
import {Employee, MnpiMetricSearchResult} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {Facepile} from 'components/DSYS/Facepile';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {type ChangeEvent, type MouseEvent, useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {useMnpiAccess} from 'views/workstreams/hooks';
import {MultiEmployeeSelect} from 'views/workstreams/TODODSYSStuff';
import {WorkstreamChatContextWorkstreamChip} from 'views/workstreams/WorkstreamChatSourceChips';

import styles from './ConfidentialMetrics.module.css';
import {
  MnpiMetricAudience,
  useMNPIMetricsAudienceService,
  useMNPIMetricSearch,
  useMNPIService,
} from './hooks';

const getPlainTextMetricTitle = (title: string) =>
  parseAndExtractTextFromLexicalJson(title).trim() || title;

const MAX_AUDIENCE_FACEPILE_COUNT = 4;

type FilterableMetric = {
  audienceEmployees: Employee[];
  pillars: string[];
  workstreams: Array<{id: number; name: string}>;
};

type MetricListFilterState = {
  audienceUserIds: Set<string>;
  pillarNames: Set<string>;
  workstreamIds: Set<number>;
};

type MetricFilterOption<T extends string | number> = {
  label: string;
  value: T;
};

const createEmptyMetricListFilters = (): MetricListFilterState => ({
  audienceUserIds: new Set(),
  pillarNames: new Set(),
  workstreamIds: new Set(),
});

const getMetricFilterTriggerLabel = <T extends string | number>(
  selectedValues: Set<T>,
  options: Array<MetricFilterOption<T>>,
  emptyLabel: string,
  countLabel: string
) => {
  if (selectedValues.size === 0) {
    return emptyLabel;
  }

  if (selectedValues.size === 1) {
    const selectedValue = selectedValues.values().next().value;
    return options.find((option) => option.value === selectedValue)?.label ?? `1 ${countLabel}`;
  }

  return `${selectedValues.size} ${countLabel}`;
};

const MetricFilterMenu = <T extends string | number>({
  label,
  searchPlaceholder,
  emptyLabel,
  countLabel,
  options,
  selectedValues,
  onSelectedValuesChange,
}: {
  label: string;
  searchPlaceholder: string;
  emptyLabel: string;
  countLabel: string;
  options: Array<MetricFilterOption<T>>;
  selectedValues: Set<T>;
  onSelectedValuesChange: (nextValues: Set<T>) => void;
}) => {
  const [searchValue, setSearchValue] = useState('');
  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const visibleOptions = normalizedSearchValue
    ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearchValue))
    : options;

  const handleToggleOption = (value: T) => {
    const nextValues = new Set(selectedValues);
    if (nextValues.has(value)) {
      nextValues.delete(value);
    } else {
      nextValues.add(value);
    }
    onSelectedValuesChange(nextValues);
  };

  return (
    <Menu.Wrapper closeOnSelection={false}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button variant="outline" withDropdownIcon {...getTriggerProps()}>
            {getMetricFilterTriggerLabel(selectedValues, options, emptyLabel, countLabel)}
          </Button>
          <Menu.Content
            {...getContentProps()}
            placement="bottom-start"
            style={{width: 320, maxWidth: 'calc(100vw - 24px)'}}
          >
            <Box padding="8" onKeyDown={(event) => event.stopPropagation()}>
              <TextInput
                name={`${label.toLowerCase().replaceAll(' ', '-')}-filter-search`}
                size="small"
                value={searchValue}
                placeholder={searchPlaceholder}
                autoComplete="off"
                withLeftAccessory={
                  <UIIcon src={SearchLine} className={atoms({color: 'Text Subtle'})} />
                }
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setSearchValue(event.currentTarget.value)
                }
              />
            </Box>
            <Menu.Segment
              withLabel={
                <Box display="flex" alignItems="center" style={{width: '100%'}}>
                  <Box style={{flex: 1, minWidth: 0}}>
                    <Text isBold size="small">
                      {label}
                    </Text>
                  </Box>
                  {selectedValues.size > 0 && (
                    <Box style={{marginLeft: 16, flexShrink: 0}}>
                      <Button
                        variant="transparent"
                        size="small"
                        aria-label={`Clear all ${label.toLowerCase()} filters`}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                          event.stopPropagation();
                          onSelectedValuesChange(new Set());
                        }}
                      >
                        Clear all
                      </Button>
                    </Box>
                  )}
                </Box>
              }
            >
              <Box className={styles.metricFilterOptions}>
                {visibleOptions.map((option) => (
                  <Menu.ActionItem
                    key={String(option.value)}
                    onClick={() => handleToggleOption(option.value)}
                    withLeftAccessory={
                      <Checkbox checked={selectedValues.has(option.value)} readOnly tabIndex={-1} />
                    }
                  >
                    {option.label}
                  </Menu.ActionItem>
                ))}
                {visibleOptions.length === 0 && (
                  <Box paddingX="16" paddingY="8">
                    <Text size="small" color="faint">
                      No matches
                    </Text>
                  </Box>
                )}
              </Box>
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const MetricListFilters = ({
  metrics,
  filters,
  onFiltersChange,
}: {
  metrics: FilterableMetric[];
  filters: MetricListFilterState;
  onFiltersChange: (filters: MetricListFilterState) => void;
}) => {
  const workstreamOptions = useMemo(() => {
    const workstreamsById = new Map<number, string>();
    metrics.forEach((metric) => {
      metric.workstreams.forEach((workstream) => {
        workstreamsById.set(workstream.id, workstream.name);
      });
    });
    return Array.from(workstreamsById, ([value, label]) => ({label, value})).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [metrics]);

  const audienceOptions = useMemo(() => {
    const employeesById = new Map<string, string>();
    metrics.forEach((metric) => {
      metric.audienceEmployees.forEach((employee) => {
        employeesById.set(employee.user_id, employee.name);
      });
    });
    return Array.from(employeesById, ([value, label]) => ({label, value})).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [metrics]);

  const pillarOptions = useMemo(
    () =>
      [...new Set(metrics.flatMap((metric) => metric.pillars))]
        .sort((a, b) => a.localeCompare(b))
        .map((pillar) => ({label: pillar, value: pillar})),
    [metrics]
  );

  const hasActiveFilters =
    filters.workstreamIds.size > 0 ||
    filters.pillarNames.size > 0 ||
    filters.audienceUserIds.size > 0;

  return (
    <Split gap="8" alignY="center" wrap="wrap">
      {workstreamOptions.length > 0 && (
        <Split.Item>
          <MetricFilterMenu
            label="Workstream"
            searchPlaceholder="Search workstreams…"
            emptyLabel="All workstreams"
            countLabel="workstreams"
            options={workstreamOptions}
            selectedValues={filters.workstreamIds}
            onSelectedValuesChange={(workstreamIds) => onFiltersChange({...filters, workstreamIds})}
          />
        </Split.Item>
      )}
      {pillarOptions.length > 0 && (
        <Split.Item>
          <MetricFilterMenu
            label="Pillar"
            searchPlaceholder="Search pillars…"
            emptyLabel="All pillars"
            countLabel="pillars"
            options={pillarOptions}
            selectedValues={filters.pillarNames}
            onSelectedValuesChange={(pillarNames) => onFiltersChange({...filters, pillarNames})}
          />
        </Split.Item>
      )}
      {audienceOptions.length > 0 && (
        <Split.Item>
          <MetricFilterMenu
            label="User with access"
            searchPlaceholder="Search users with access…"
            emptyLabel="All users with access"
            countLabel="users with access"
            options={audienceOptions}
            selectedValues={filters.audienceUserIds}
            onSelectedValuesChange={(audienceUserIds) =>
              onFiltersChange({...filters, audienceUserIds})
            }
          />
        </Split.Item>
      )}
      {hasActiveFilters && (
        <Split.Item>
          <Button
            variant="transparent"
            onClick={() => onFiltersChange(createEmptyMetricListFilters())}
          >
            Clear filters
          </Button>
        </Split.Item>
      )}
    </Split>
  );
};

const filterMetrics = <T extends FilterableMetric>(metrics: T[], filters: MetricListFilterState) =>
  metrics.filter((metric) => {
    const matchesWorkstream =
      filters.workstreamIds.size === 0 ||
      metric.workstreams.some((workstream) => filters.workstreamIds.has(workstream.id));
    const matchesAudience =
      filters.audienceUserIds.size === 0 ||
      metric.audienceEmployees.some((employee) => filters.audienceUserIds.has(employee.user_id));
    const matchesPillar =
      filters.pillarNames.size === 0 ||
      metric.pillars.some((pillar) => filters.pillarNames.has(pillar));

    return matchesWorkstream && matchesPillar && matchesAudience;
  });

const getAudienceCountLabel = (audienceCount: number) => {
  if (audienceCount === 0) {
    return 'No audience';
  }

  return audienceCount === 1 ? '1 person' : `${audienceCount} people`;
};

const getAudienceSummaryLabel = (audienceCount: number) => {
  const hiddenAudienceCount = audienceCount - MAX_AUDIENCE_FACEPILE_COUNT;

  if (hiddenAudienceCount > 0) {
    return `+ ${hiddenAudienceCount} more`;
  }

  return getAudienceCountLabel(audienceCount);
};

export const ConfidentialMetrics = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const {hasMnpiAccess, isLoading: isMnpiAccessLoading} = useMnpiAccess();

  useDocumentTitle('Confidential metrics');

  if (!pulseUser) {
    return null;
  }

  const canManageMnpiAdmins = isUserAdmin(pulseUser);

  if (!canManageMnpiAdmins && isMnpiAccessLoading) {
    return null;
  }

  if (!canManageMnpiAdmins && !hasMnpiAccess) {
    navigate('/');
    return null;
  }

  return (
    <MNPICustomAudiencesAdminPage
      canManageMnpiAdmins={canManageMnpiAdmins}
      hasMnpiAccess={hasMnpiAccess}
    />
  );
};

const MNPICustomAudiencesAdminPage = ({
  canManageMnpiAdmins,
  hasMnpiAccess,
}: {
  canManageMnpiAdmins: boolean;
  hasMnpiAccess: boolean;
}) => {
  const {allowedEmployees, addEmployee, removeEmployee, isError, isLoading} = useMNPIService();

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Confidential metrics', to: ROUTE_PATHS.ADMIN_CONFIDENTIAL_METRICS},
      ]}
    >
      <Stack gap="24">
        <Title size={24}>Confidential metrics</Title>
        <MNPIAdminsEditor
          employees={allowedEmployees}
          onAddEmployee={addEmployee}
          onRemoveEmployee={removeEmployee}
          canManageMnpiAdmins={canManageMnpiAdmins}
          isLoading={isLoading}
          isError={isError}
        />
        {hasMnpiAccess && <MNPIMetricsAudienceSection />}
      </Stack>
    </Layout.Container>
  );
};

const MNPIMetricsAudienceSection = () => {
  const {metrics, updateMetricAudience, markMetric, unmarkMetric, isError, isLoading} =
    useMNPIMetricsAudienceService();
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const [expandedMetricId, setExpandedMetricId] = useState<number | null>(null);
  const [metricPendingUnmark, setMetricPendingUnmark] = useState<{
    metricId: number;
  } | null>(null);
  const [isUnmarkingMetric, setIsUnmarkingMetric] = useState(false);
  const [filters, setFilters] = useState<MetricListFilterState>(createEmptyMetricListFilters);
  const filteredMetrics = useMemo(() => filterMetrics(metrics, filters), [filters, metrics]);

  const handleMarkMetric = useCallback(
    (metric: MnpiMetricSearchResult) => {
      void (async () => {
        try {
          await markMetric(metric.metric_id);
          setSnackbarMessage({text: t('saved')});
        } catch {
          setSnackbarMessage({text: 'Failed to mark metric as confidential.'});
        }
      })();
    },
    [markMetric, setSnackbarMessage]
  );

  const handleAudienceChange = useCallback(
    (metric: MnpiMetricAudience, nextEmployees: Employee[]) => {
      void (async () => {
        try {
          await updateMetricAudience(metric, nextEmployees);
          setSnackbarMessage({text: t('saved')});
        } catch {
          setSnackbarMessage({text: 'Failed to update metric audience.'});
        }
      })();
    },
    [setSnackbarMessage, updateMetricAudience]
  );

  const handleUnmarkMetric = useCallback(
    async (metricId: number) => {
      setIsUnmarkingMetric(true);
      try {
        await unmarkMetric(metricId);
        setExpandedMetricId((currentMetricId) =>
          currentMetricId === metricId ? null : currentMetricId
        );
        setMetricPendingUnmark(null);
        setSnackbarMessage({text: t('saved')});
      } catch {
        setSnackbarMessage({text: 'Failed to remove confidential status.'});
      } finally {
        setIsUnmarkingMetric(false);
      }
    },
    [setSnackbarMessage, unmarkMetric]
  );

  const handleAudiencePopoverToggle = useCallback((metricId: number, isOpen: boolean) => {
    setExpandedMetricId((currentMetricId) => {
      if (isOpen) {
        return metricId;
      }

      return currentMetricId === metricId ? null : currentMetricId;
    });
  }, []);

  return (
    <>
      <Stack gap="16">
        <Title size={16}>Metrics</Title>

        <div className={styles.metricSearchToolbar}>
          <MNPIMetricSearch onSelectMetric={handleMarkMetric} />
          {!isLoading && !isError && metrics.length > 0 && (
            <MetricListFilters metrics={metrics} filters={filters} onFiltersChange={setFilters} />
          )}
        </div>

        {isLoading ? (
          <Box paddingY="8">
            <Text color="faint">Loading confidential metrics...</Text>
          </Box>
        ) : isError ? (
          <Box paddingY="8">
            <Text color="faint">Unable to load confidential metrics.</Text>
          </Box>
        ) : metrics.length === 0 ? (
          <Box paddingY="8">
            <Text color="faint">No confidential metrics yet.</Text>
          </Box>
        ) : filteredMetrics.length === 0 ? (
          <Box paddingY="8">
            <Text color="faint">No confidential metrics match these filters.</Text>
          </Box>
        ) : (
          <Box
            borderRadius="Medium"
            borderWidth="1"
            borderStyle="Solid"
            borderColor="Border Subtle"
            backgroundColor="Background Base"
          >
            {filteredMetrics.map((metric, index) => {
              const metricTitle = getPlainTextMetricTitle(metric.title);
              const audienceCount = metric.audienceEmployees.length;
              const visibleAudienceEmployees = metric.audienceEmployees.slice(
                0,
                MAX_AUDIENCE_FACEPILE_COUNT
              );
              const isExpanded = expandedMetricId === metric.metric_id;

              return (
                <Box
                  key={metric.metric_id}
                  borderBottom={index === filteredMetrics.length - 1 ? undefined : 'Solid'}
                  borderColor="Border Subtle"
                >
                  <Stack gap="0">
                    <Split
                      className={styles.metricAudienceRow}
                      alignY="center"
                      gap="8"
                      paddingX="16"
                      paddingY="16"
                    >
                      <Split.Item width="fill">
                        <Stack gap="8">
                          <Text isBold>{metricTitle}</Text>
                          {metric.workstreams.length > 0 && (
                            <Split gap="4" wrap="wrap">
                              {metric.workstreams.map((workstream) => (
                                <Split.Item key={workstream.id}>
                                  <WorkstreamChatContextWorkstreamChip
                                    name={workstream.name}
                                    tooltipTitle={`${workstream.name}`}
                                    onClick={() =>
                                      window.open(
                                        ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(
                                          ':id',
                                          workstream.id.toString()
                                        ),
                                        '_blank',
                                        'noopener,noreferrer'
                                      )
                                    }
                                  />
                                </Split.Item>
                              ))}
                            </Split>
                          )}
                        </Stack>
                      </Split.Item>
                      <Split.Item>
                        <Popover
                          triggerStrategy="click"
                          open={isExpanded}
                          onToggle={(isOpen) =>
                            handleAudiencePopoverToggle(metric.metric_id, isOpen)
                          }
                          placement="bottom-end"
                          offsetDistance={8}
                        >
                          <Popover.Trigger asChild>
                            <button
                              type="button"
                              className={styles.audienceSummaryButton}
                              aria-label={`Edit audience for ${metricTitle}: ${getAudienceCountLabel(
                                audienceCount
                              )}`}
                            >
                              {visibleAudienceEmployees.length > 0 && (
                                <Facepile
                                  ldaps={visibleAudienceEmployees.map((employee) => employee.ldap)}
                                  size="small"
                                  overflow={MAX_AUDIENCE_FACEPILE_COUNT}
                                  hideTooltip
                                  shouldSort={false}
                                />
                              )}
                              <Text color="faint" size="small">
                                {getAudienceSummaryLabel(audienceCount)}
                              </Text>
                            </button>
                          </Popover.Trigger>
                          <Popover.Overlay>
                            <Popover.Content className={styles.metricAudienceEditor}>
                              <Popover.Body>
                                <Stack gap="8">
                                  <Split alignY="center">
                                    <Split.Item width="fill">
                                      <Text isBold size="small">
                                        Audience
                                      </Text>
                                    </Split.Item>
                                    <Split.Item>
                                      <Button
                                        variant="transparent"
                                        size="small"
                                        disabled={audienceCount === 0}
                                        aria-label={`Clear all audience members for ${metricTitle}`}
                                        onClick={() => handleAudienceChange(metric, [])}
                                      >
                                        Clear all
                                      </Button>
                                    </Split.Item>
                                  </Split>
                                  <MultiEmployeeSelect
                                    selections={metric.audienceEmployees}
                                    setSelections={(nextEmployees) =>
                                      handleAudienceChange(metric, nextEmployees)
                                    }
                                    keepInputVisible
                                    startEditing
                                    autoFocus={false}
                                    resultsPlacement="bottom-end"
                                  />
                                </Stack>
                              </Popover.Body>
                            </Popover.Content>
                          </Popover.Overlay>
                        </Popover>
                      </Split.Item>
                      <Split.Item>
                        <IconButton
                          variant="borderless"
                          size="small"
                          aria-label={`Remove ${metricTitle} from Confidential Metrics`}
                          onClick={() =>
                            setMetricPendingUnmark({
                              metricId: metric.metric_id,
                            })
                          }
                        >
                          <UIIcon src={DeleteLine} className={atoms({color: 'Text Subtle'})} />
                        </IconButton>
                      </Split.Item>
                    </Split>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      </Stack>

      {metricPendingUnmark && (
        <Modal
          open
          withCloseButton="close"
          width="small"
          aria-label="Remove from confidential metrics?"
          onRequestClose={() => {
            if (!isUnmarkingMetric) {
              setMetricPendingUnmark(null);
            }
          }}
        >
          <Modal.Header>
            <Modal.Title>Remove from confidential metrics?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            This metric will no longer be confidential, and it will become accessible to everyone.
          </Modal.Body>
          <Modal.Footer>
            <CancelButton onClick={() => setMetricPendingUnmark(null)} disabled={isUnmarkingMetric}>
              {t('cancel')}
            </CancelButton>
            <Button
              aria-label="Confirm remove from confidential metrics"
              variant="primary"
              isLoading={isUnmarkingMetric}
              onClick={() => void handleUnmarkMetric(metricPendingUnmark.metricId)}
            >
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

const MNPIMetricSearch = ({
  onSelectMetric,
}: {
  onSelectMetric: (metric: MnpiMetricSearchResult) => void;
}) => {
  const [searchValue, setSearchValue] = useState('');
  const {metrics, isError, isLoading, query} = useMNPIMetricSearch(searchValue);

  const handleSelection = useCallback(
    (metricId: string) => {
      const selectedMetric = metrics.find(
        (metric) => String(metric.metric_id) === String(metricId)
      );

      if (!selectedMetric || selectedMetric.is_mnpi) {
        return;
      }

      onSelectMetric(selectedMetric);
      setSearchValue('');
    },
    [metrics, onSelectMetric]
  );

  const renderRow = (metric: MnpiMetricSearchResult) => {
    const metricTitle = getPlainTextMetricTitle(metric.title).trim() || t('metric').toString();

    return (
      <Typeahead.Row
        key={metric.metric_id}
        value={String(metric.metric_id)}
        disabled={metric.is_mnpi}
        withTitle={<Text size="small">{metricTitle}</Text>}
        withSubtitle={
          <Text size="small" color={metric.is_mnpi ? 'disabled' : 'faint'}>
            {metric.is_mnpi ? 'Already confidential' : 'Select to mark as confidential'}
          </Text>
        }
        aria-label={`${metricTitle} ${metric.is_mnpi ? 'Already confidential' : 'Select to mark as confidential'}}`}
      />
    );
  };

  const showEmptyState = query.length > 0 && !isLoading && !isError && metrics.length === 0;

  return (
    <Typeahead.Wrapper isPortaled hasMaxHeight={320} openMenuOnFocus onSelection={handleSelection}>
      {({getContentProps, getTriggerProps, openMenu}) => (
        <Box>
          <TextInput
            name="mnpi-metric-typeahead"
            size="small"
            value={searchValue}
            placeholder="Search metrics…"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            withLeftAccessory={
              <UIIcon src={SearchLine} className={atoms({color: 'Text Subtle'})} />
            }
            withRightAccessory={isLoading ? <Spinner size="small" /> : undefined}
            {...getTriggerProps({
              onChange: (event) => setSearchValue((event.currentTarget as HTMLInputElement).value),
              onClick: () => openMenu({autoFocus: false}),
              onKeyDown: (event) => event.stopPropagation(),
            })}
          />

          <Typeahead.Container
            {...getContentProps()}
            auto
            placement="bottom-start"
            triggerOffset={8}
            elementResize
          >
            {isError ? (
              <Typeahead.Row disabled>
                <Box padding="8">
                  <Text size="small" color="error">
                    Unable to search metrics.
                  </Text>
                </Box>
              </Typeahead.Row>
            ) : metrics.length > 0 ? (
              <Typeahead.Results
                initialResults={6}
                maxResults={metrics.length}
                results={metrics}
                renderRow={renderRow}
              />
            ) : showEmptyState ? (
              <Typeahead.Row disabled>
                <Box padding="8">
                  <Text size="small" color="faint">
                    No matching metrics found.
                  </Text>
                </Box>
              </Typeahead.Row>
            ) : null}
          </Typeahead.Container>
        </Box>
      )}
    </Typeahead.Wrapper>
  );
};

const MNPIAdminsEditor = ({
  employees,
  onAddEmployee,
  onRemoveEmployee,
  canManageMnpiAdmins,
  isLoading,
  isError,
}: {
  employees: Employee[];
  onAddEmployee: (employee: Employee) => Promise<void>;
  onRemoveEmployee: (userId: string) => Promise<void>;
  canManageMnpiAdmins: boolean;
  isLoading: boolean;
  isError: boolean;
}) => {
  const setSnackbarMessage = useSetAtom(snackbarAtom);

  const handleSelectionsChange = useCallback(
    (nextEmployees: Employee[]) => {
      if (!canManageMnpiAdmins) {
        return;
      }

      const currentUserIds = new Set(employees.map((employee) => employee.user_id));
      const nextUserIds = new Set(nextEmployees.map((employee) => employee.user_id));
      const employeesToAdd = nextEmployees.filter(
        (employee) => !currentUserIds.has(employee.user_id)
      );
      const employeeIdsToRemove = employees
        .filter((employee) => !nextUserIds.has(employee.user_id))
        .map((employee) => employee.user_id);

      if (employeesToAdd.length === 0 && employeeIdsToRemove.length === 0) {
        return;
      }

      void (async () => {
        try {
          await Promise.all([
            ...employeesToAdd.map((employee) => onAddEmployee(employee)),
            ...employeeIdsToRemove.map((userId) => onRemoveEmployee(userId)),
          ]);
          setSnackbarMessage({text: t('saved')});
        } catch {
          setSnackbarMessage({text: 'Failed to update Confidential Metrics admins.'});
        }
      })();
    },
    [canManageMnpiAdmins, employees, onAddEmployee, onRemoveEmployee, setSnackbarMessage]
  );

  return (
    <Stack gap="16">
      <Split alignY="center" gap="4">
        <Split.Item>
          <Title size={16}>Admins</Title>
        </Split.Item>
        <Split.Item>
          <Tooltip
            title="Admins can view all confidential metrics, toggle metrics as confidential, and manage per-metric audiences."
            placement="top"
          >
            <IconButton
              variant="borderless"
              size="small"
              aria-label="Admins can view all confidential metrics, toggle metrics as confidential, and manage per-metric audiences."
            >
              <UIIcon src={InfoLine} size="small" className={atoms({color: 'Text Subtle'})} />
            </IconButton>
          </Tooltip>
        </Split.Item>
      </Split>

      {isLoading ? (
        <Box paddingY="8">
          <Text color="faint">Loading admins...</Text>
        </Box>
      ) : isError ? (
        <Box paddingY="8">
          <Text color="faint">Unable to load admins.</Text>
        </Box>
      ) : !canManageMnpiAdmins && employees.length === 0 ? (
        <Box paddingY="8">
          <Text color="faint">No admins found.</Text>
        </Box>
      ) : (
        <MultiEmployeeSelect
          selections={[...employees].sort((a, b) => a.name.localeCompare(b.name))}
          setSelections={handleSelectionsChange}
          disabled={!canManageMnpiAdmins}
          autoFocus={false}
        />
      )}
    </Stack>
  );
};
